import { useEffect, useState } from 'react';
import * as yup from 'yup'; // Import yup
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import { getProperties, getPropertyUtilities, getUtility, getMeters, getProperty } from '../../Services/PortfolioService';
import { Col, Form, FormGroup, Input, Label, Row } from 'reactstrap'
import { addBills } from '../../Services/BillService';
import { toast } from 'react-toastify';
import { sortByKey } from '../../Utils';

const FILE_SIZE_LIMIT = 5 * 1024 * 1024; // 5MB
const SUPPORTED_FORMATS = [
    'application/pdf',
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'image/webp',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/csv',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

const schema = yup.object().shape({
    company_id: yup.string().trim().required('Client is required'),
    property_id: yup.string().trim().required('Property is required'),
    utility_id: yup.string().trim().required('Utility is required'),
    supplier_id: yup.string().trim().required('Supplier ID is required'),
    site_id: yup.string().trim().required('Meter is required'),
    total_consumption: yup.string().required('Total consumption is required'),
    cost: yup.string().required('Cost is required'),
    bill_date: yup.string().trim().required('Bill date is required'),
    start_date: yup.string().trim().required('Start date is required'),
    end_date: yup.string().trim().required('End date is required'),
    month: yup.string().required('Month is required'),
    days: yup.number().required('Days is required'),
    year: yup.number().required('Year is required'),
    bill_type: yup.string().trim().required('Bill type is required'),
    notes: yup.string().trim().optional(),
    bill_document: yup
        .mixed()
        // .required('File is required')
        .notRequired()
        .test('fileSize', 'File size must be less than 5MB', (value) => {
            console.log('Validating fileSize:', value);
            if (!value || !(value as FileList)[0]) return true;
            return value && (value as FileList)[0]?.size <= FILE_SIZE_LIMIT;
        })
        .test('fileFormat', 'Unsupported file format', (value) => {
            console.log('Validating file format:', value);
            if (!value || !(value as FileList)[0]) return true;
            return value && SUPPORTED_FORMATS.includes((value as FileList)[0]?.type);
        }),
    status: yup.string().oneOf(['in_review', 'pay', 'dont_pay']),
    comment: yup.string().trim().optional(),
});

export const CreateBillForm = ({ clientId, reloadData }: { clientId: string, reloadData: () => void }) => {
    const defaultValues = {
        company_id: clientId,
        property_id: '',
        utility_id: '',
        supplier_id: '',
        site_id: '',
        total_consumption: '',
        cost: '',
        bill_date: '',
        start_date: '',
        end_date: '',
        month: '',
        days: 0,
        year: 0,
        bill_type: '',
        notes: '',
        bill_document: null,
        status: 'in_review',
        comment: ''
    };

    const { control, watch, setValue, handleSubmit, formState: { errors }, reset } = useForm({
        resolver: yupResolver(schema), // Use the schema for validation
        defaultValues: defaultValues
    });
    const [properties, setProperties] = useState<any[]>([]);
    const [meters, setMeters] = useState<any[]>([]);
    const [utilities, setUtilities] = useState<any[]>([]);
    const [suppliers, setSuppliers] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    const siteId = watch('site_id');
    const propertyId = watch('property_id');
    const utilityId = watch('utility_id');

    // Fetch properties when client changes
    useEffect(() => {
        if (clientId) {
            reset({
                ...defaultValues,
                company_id: clientId, // keep this in case it changes dynamically
            });
            setValue('company_id', clientId);
            fetchProperties(clientId).then(data => setProperties(sortByKey(data, 'name')));
        }
    }, [clientId]);

    // Fetch meters/utilities when property changes
    useEffect(() => {
        if (clientId && propertyId) {
            fetchPropertyMeters(clientId, propertyId).then(data => setMeters(sortByKey(data, 'name')));
            fetchPropertyUtilities(clientId, propertyId).then(data => setUtilities(sortByKey(data, 'name')));
        }
    }, [clientId, propertyId]);

    // Fetch suppliers when utility changes
    useEffect(() => {
        if (utilityId) fetchUtility(utilityId).then(data => setSuppliers(sortByKey(data.suppliers, 'name')));
    }, [utilityId]);

    const fetchProperties = async (clientId: string) => {
        if (clientId == '') return
        const { properties } = await getProperties(clientId);
        return properties
    }

    const fetchPropertyMeters = async (selectedClient: string, property_id: string) => {
        try {
            const response = await getProperty(selectedClient, property_id);
            return response.meters;
        } catch (error) {
            console.error("Error fetching meters:", error);
        }
    };

    const fetchPropertyUtilities = async (selectedClient: string, property_id: string) => {
        try {
            const data = await getPropertyUtilities(selectedClient, property_id);
            return data;
        } catch (error) {
            console.error("Error fetching utilities:", error);
        }
    };

    const fetchUtility = async (utility_id: string) => {
        try {
            const data = await getUtility(utility_id);
            return data;
        } catch (error) {
            console.error("Error fetching utility suppliers:", error);
        }
    };

    const startDate = watch('start_date');
    const endDate = watch('end_date');

    useEffect(() => {
        if (!startDate || !endDate) return;

        const start = new Date(startDate);
        const end = new Date(endDate);

        const days = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
        const month = String(end.getMonth() + 1);
        const year = end.getFullYear();

        setValue("days", days)
        setValue("month", month)
        setValue('year', year)
    }, [startDate, endDate, setValue]);

    useEffect(() => {
        console.log('Form errors:', errors);
    }, [errors]);

    const onSubmit: SubmitHandler<any> = async (data) => {
        setLoading(true)
        // console.log('submitting:', data)
        try {
            const formData = new FormData();

            // Add all form fields to FormData
            Object.keys(data).forEach(key => {
                if (key === 'bill_document' && data[key]) {
                    // Handle file upload
                    for (let i = 0; i < data[key].length; i++) {
                        formData.append('bill_document', data[key][i]);
                    }
                } else {
                    formData.append(key, data[key]);
                }
            });

            const response = await addBills(formData);
            // console.log(response);
            // Handle success response
            toast.success(response.message || "Bill added successfully");
            reloadData();
            reset()
        } catch (error: any) {
            console.error(error);
            // Handle error response
            toast.error(error.message || "Error adding Bill");
        } finally {
            setLoading(false);
        }
    }

    return (
        <Form className="theme-form mt-4 text-start" onSubmit={handleSubmit(onSubmit)}>
            <Row>
                <Col sm={4}>
                    <FormGroup>
                        <Col><Label>Property</Label><span className="text-danger">*</span></Col>
                        <Controller
                            name="property_id"
                            control={control}
                            render={({ field }) => (
                                <Input type="select" {...field}>
                                    <option selected value=''>Select Property</option>
                                    {properties.map((item: { property_id: string; name: string }, i: number) => (
                                        <option key={i} value={item.property_id}>{item.name}</option>
                                    ))}
                                </Input>
                            )}
                        />
                        {errors.property_id && <p className='text-danger'>{errors.property_id.message}</p>} {/* Error message */}
                    </FormGroup>
                </Col>
                <Col sm={4}>
                    <FormGroup>
                        <Col><Label>Meter</Label><span className="text-danger">*</span></Col>
                        <Controller
                            name="site_id"
                            control={control}
                            render={({ field }) => (
                                <Input type="select" {...field}>
                                    <option selected value=''>Select Meter</option>
                                    {meters.map((item: { site_id: string; name: string }, i: number) => (
                                        <option key={i} value={item.site_id}>{item.name}</option>
                                    ))}
                                </Input>
                            )}
                        />
                        {errors.site_id && <p className='text-danger'>{errors.site_id.message}</p>} {/* Error message */}
                    </FormGroup>
                </Col>
                <Col sm={4}>
                    <FormGroup>
                        <Col><Label>Site ID</Label></Col>
                        <Input
                            type="text"
                            value={siteId}
                            readOnly
                            placeholder='Add any additional information about the bill...'
                        />
                    </FormGroup>
                </Col>
                <Col sm={4}>
                    <FormGroup>
                        <Col><Label>Utility</Label><span className="text-danger">*</span></Col>
                        <Controller
                            name="utility_id"
                            control={control}
                            render={({ field }) => (
                                <Input type="select" {...field}>
                                    <option selected value=''>Select Utility</option>
                                    {utilities.map((item: { utility_id: string; name: string }, i: number) => (
                                        <option key={i} value={item.utility_id}>{item.name}</option>
                                    ))}
                                </Input>
                            )}
                        />
                        {errors.utility_id && <p className='text-danger'>{errors.utility_id.message}</p>} {/* Error message */}
                    </FormGroup>
                </Col>
                <Col sm={4}>
                    <FormGroup>
                        <Col><Label>Suppliers</Label><span className="text-danger">*</span></Col>
                        <Controller
                            name="supplier_id"
                            control={control}
                            render={({ field }) => (
                                <Input type="select" {...field}>
                                    <option selected value=''>Select Supplier</option>
                                    {suppliers.map((item: { supplier_id: string; name: string }, i: number) => (
                                        <option key={i} value={item.supplier_id}>{item.name}</option>
                                    ))}
                                </Input>
                            )}
                        />
                        {errors.supplier_id && <p className='text-danger'>{errors.supplier_id.message}</p>} {/* Error message */}
                    </FormGroup>
                </Col>
                <Col sm={4}>
                    <FormGroup>
                        <Col><Label>Total Consumption</Label></Col>
                        <Controller
                            name="total_consumption"
                            control={control}
                            render={({ field }) => (
                                <Input
                                    type="text"
                                    pattern="-?[0-9]*([.,][0-9]{1,3})?"
                                    placeholder='Enter the Total Consumption'
                                    {...field}
                                />
                            )}
                        />
                        {errors.total_consumption && <p className='text-danger'>{errors.total_consumption.message}</p>} {/* Error message */}
                    </FormGroup>
                </Col>
                <Col sm={4}>
                    <FormGroup>
                        <Col><Label>Cost</Label></Col>
                        <Controller
                            name="cost"
                            control={control}
                            render={({ field }) => (
                                <Input
                                    type="text"
                                    pattern="-?[0-9]*([.,][0-9]{1,2})?"
                                    placeholder='Enter the cost'
                                    {...field}
                                />
                            )}
                        />
                        {errors.cost && <p className='text-danger'>{errors.cost.message}</p>} {/* Error message */}
                    </FormGroup>
                </Col>
                <Col sm={4}>
                    <FormGroup>
                        <Col>
                            <Label>Bill Type</Label>
                        </Col>
                        <Controller
                            name="bill_type"
                            control={control}
                            render={({ field }) => (
                                <Input {...field} type="select" tag="select" className="form-control">
                                    <option value="">Select Bill Type</option>
                                    {[
                                        { label: "Actual", value: 'actual' }, { label: "Credit", value: 'credit' }, { label: "Estimated", value: 'estimated' }
                                    ].map((type, index) => (
                                        <option key={index} value={type.value}>{type.label}</option>
                                    ))}
                                </Input>
                            )}
                        />
                        {errors.month && <p className="text-danger">{errors.month.message}</p>}
                    </FormGroup>
                </Col>
                <Col sm={4}>
                    <FormGroup>
                        <Col><Label>Bill Date</Label></Col>
                        <Controller
                            name="bill_date"
                            control={control}
                            render={({ field }) => (
                                <Input
                                    type="date"
                                    placeholder='Select the date'
                                    {...field}
                                />
                            )}
                        />
                        {errors.bill_date && <p className='text-danger'>{errors.bill_date.message}</p>} {/* Error message */}
                    </FormGroup>
                </Col>
                <Col sm={3}>
                    <FormGroup>
                        <Col><Label>Start Date</Label></Col>
                        <Controller
                            name="start_date"
                            control={control}
                            render={({ field }) => (
                                <Input
                                    type="date"
                                    placeholder='Select the date'
                                    {...field}
                                />
                            )}
                        />
                        {errors.start_date && <p className='text-danger'>{errors.start_date.message}</p>} {/* Error message */}
                    </FormGroup>
                </Col>
                <Col sm={3}>
                    <FormGroup>
                        <Col><Label>End Date</Label></Col>
                        <Controller
                            name="end_date"
                            control={control}
                            render={({ field }) => (
                                <Input
                                    type="date"
                                    placeholder='Select the date'
                                    {...field}
                                />
                            )}
                        />
                        {errors.end_date && <p className='text-danger'>{errors.end_date.message}</p>} {/* Error message */}
                    </FormGroup>
                </Col>
                <Col sm={2}>
                    <FormGroup>
                        <Col><Label>Days</Label></Col>
                        <Controller
                            name="days"
                            control={control}
                            render={({ field }) => (
                                <Input {...field} type="number" pattern="[0-9]*" label="Days" />
                            )}
                        />
                        {errors.days && <p className='text-danger'>{errors.days.message}</p>} {/* Error message */}
                    </FormGroup>
                </Col>
                <Col sm={2}>
                    <FormGroup>
                        <Col>
                            <Label>Month</Label>
                        </Col>
                        <Controller
                            name="month"
                            control={control}
                            render={({ field }) => (
                                <Input {...field} type="select" tag="select" className="form-control">
                                    <option value="">Select Month</option>
                                    {[
                                        "January", "February", "March", "April", "May", "June",
                                        "July", "August", "September", "October", "November", "December"
                                    ].map((month, index) => (
                                        <option key={index} value={index + 1}>{month}</option>
                                    ))}
                                </Input>
                            )}
                        />
                        {errors.month && <p className="text-danger">{errors.month.message}</p>}
                    </FormGroup>
                </Col>
                <Col sm={2}>
                    <FormGroup>
                        <Col><Label>Year</Label></Col>
                        <Controller
                            name="year"
                            control={control}
                            render={({ field }) => (
                                <Input {...field} type="number" pattern="[0-9]*" label="Days Between" />
                            )}
                        />
                        {errors.year && <p className='text-danger'>{errors.year.message}</p>} {/* Error message */}
                    </FormGroup>
                </Col>
                <Col sm={7}>
                    <FormGroup>
                        <Label>Notes</Label>
                        <Controller
                            name="notes"
                            control={control}
                            render={({ field }) => (
                                <Input {...field} type="textarea" rows="3" placeholder="Add notes about the bill..." />
                            )}
                        />
                        {errors.notes && <p className='text-danger'>{errors.notes.message}</p>} {/* Error message */}
                    </FormGroup>
                </Col>
                <Col sm={5}>
                    <FormGroup>
                        <Col><Label>Upload File</Label></Col>
                        <Controller
                            name="bill_document"
                            control={control}
                            render={({ field: { onChange } }) => (
                                <Input
                                    type="file"
                                    className={`form-control ${errors.bill_document ? 'is-invalid' : ''}`}
                                    onChange={(e) => {
                                        const files = e.target.files;
                                        onChange(files);
                                    }}
                                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.csv,.xls,.xlsx"
                                />
                            )}
                        />
                        {errors.bill_document && <p className='text-danger'>{errors.bill_document.message}</p>}
                    </FormGroup>
                </Col>
                <Col sm={5}>
                    <FormGroup>
                        <Label>Review Status</Label>
                        <Controller
                            name="status"
                            control={control}
                            render={({ field }) => (
                                <Input {...field} type="select" placeholder="Select status">
                                    <option value="">Select status</option>
                                    <option value="in_review">In Review</option>
                                    <option value="pay">Pay</option>
                                    <option value="dont_pay">Don't Pay</option>
                                </Input>
                            )}
                        />
                        {errors.status && <p className='text-danger'>{errors.status.message}</p>} {/* Error message */}
                    </FormGroup>
                </Col>
                <Col sm={7}>
                    <FormGroup>
                        <Label>Review Comment</Label>
                        <Controller
                            name="comment"
                            control={control}
                            render={({ field }) => (
                                <Input {...field} type="textarea" rows="3" placeholder="Add review comment about the bill..." />
                            )}
                        />
                        {errors.comment && <p className='text-danger'>{errors.comment.message}</p>} {/* Error message */}
                    </FormGroup>
                </Col>
            </Row>
            <div className="form-actions mt-3 text-end">
                <button type="submit" className="btn btn-primary mx-2" disabled={loading}>Submit</button>
                <button type="reset" className="btn btn-secondary mx-2" onClick={() => { reset(); }}>Reset</button>
            </div>
        </Form>
    )
}